import { AbstractApiDataSource, PageInfo } from './abstract.api-data-source';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { debounceTime, delay, startWith } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { Filter, Pagination, SortDir } from '../services/abstract-api.service';
import { clone } from 'lodash-es';

export class TableApiDataSource<ItemType> extends AbstractApiDataSource<ItemType> {
    private paginator: MatPaginator;
    private matSort: MatSort;
    private filterObservable: Observable<Filter>;

    private subscriptions: Subscription[] = [];

    public get Data(): ItemType[] {
        return this.cachedData;
    }

    public set Data(data: ItemType[]) {
        this.cachedData = clone(data);
        this.dataSubject.next(data);
    }

    public get HasSeenData(): boolean {
        return this.hasSeenData;
    }

    public get Paginator(): MatPaginator {
        return this.paginator;
    }

    public set ManualPagination(value: Pagination | null) {
        this.Pagination = value;
    }

    set MatPaginator(paginator: MatPaginator) {
        this.paginator = paginator;

        const paginatorSubscription = this.paginator.page
            .pipe(
                delay(0),
                startWith({
                    pageIndex: 0,
                    pageSize: this.paginator.pageSize,
                    length: 0,
                }),
            )
            .subscribe({
                next: (page: PageEvent) => {
                    this.Pagination = {
                        page: page.pageIndex + 1,
                        limit: page.pageSize,
                    };
                },
            });

        this.subscriptions.push(paginatorSubscription);
    }

    set MatSort(sort: MatSort) {
        this.matSort = sort;

        const sortSubscription = this.matSort.sortChange.subscribe(() => {
            const hasSort = this.matSort.direction !== '';

            if (!hasSort) {
                this.Sort = null;
                return;
            }

            const type: SortDir = this.matSort.direction === 'asc' ? SortDir.Asc : SortDir.Desc;

            this.Sort = {
                field: this.matSort.active,
                type,
            };
        });

        this.subscriptions.push(sortSubscription);
    }

    set FilterObservable(filter: Observable<Filter>) {
        this.filterObservable = filter;

        const filterSubscription = this.filterObservable.pipe(debounceTime(200)).subscribe({
            next: (value: Filter) => {
                this.Filter = value;
                // changing the filters should reset the pagination to first page
                this.Paginator.page.next({
                    pageIndex: 0,
                    pageSize: this.paginator.pageSize,
                    length: this.paginator.length,
                });
            },
        });

        this.subscriptions.push(filterSubscription);
    }

    public connect(): Observable<ItemType[]> {
        this.subscriptions.push(
            this.PageInfo$.subscribe({
                next: (pageInfo: PageInfo) => {
                    if (!this.paginator) {
                        return;
                    }

                    this.paginator.length = pageInfo.totalItems;
                },
            }),
        );

        return super.connect();
    }

    public disconnect(): void {
        for (const sub of this.subscriptions) {
            sub?.unsubscribe();
        }

        super.disconnect();
    }

    public refreshCurrentPage(): void {
        this.refreshPageSubject.next();
    }
}
